import { crudOperations } from './api';

/**
 * Service for canvassing data system — categories, scraping, leads
 */
const canvassingService = {
  // ─── Categories ───────────────────────────────────────────

  async getCategories() {
    try {
      const response = await crudOperations.get('/canvassing/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  async createCategory(data) {
    try {
      const response = await crudOperations.post('/canvassing/categories', data);
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  async updateCategory(id, data) {
    try {
      const response = await crudOperations.put(`/canvassing/categories/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },

  async deleteCategory(id) {
    try {
      const response = await crudOperations.delete(`/canvassing/categories/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },

  async addPhrase(categoryId, data) {
    try {
      const response = await crudOperations.post(`/canvassing/categories/${categoryId}/phrases`, data);
      return response.data;
    } catch (error) {
      console.error('Error adding phrase:', error);
      throw error;
    }
  },

  async updatePhrase(categoryId, phraseId, data) {
    try {
      const response = await crudOperations.put(`/canvassing/categories/${categoryId}/phrases/${phraseId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating phrase:', error);
      throw error;
    }
  },

  async deletePhrase(categoryId, phraseId) {
    try {
      const response = await crudOperations.delete(`/canvassing/categories/${categoryId}/phrases/${phraseId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting phrase:', error);
      throw error;
    }
  },

  async seedCategories() {
    try {
      const response = await crudOperations.post('/canvassing/categories/seed');
      return response.data;
    } catch (error) {
      console.error('Error seeding categories:', error);
      throw error;
    }
  },

  // ─── Scraping ─────────────────────────────────────────────

  async startScrape(data) {
    try {
      const response = await crudOperations.post('/canvassing/scrape/start', data);
      return response.data;
    } catch (error) {
      console.error('Error starting scrape:', error);
      throw error;
    }
  },

  async cancelScrape(jobId) {
    try {
      const response = await crudOperations.post(`/canvassing/scrape/${jobId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling scrape:', error);
      throw error;
    }
  },

  async getScrapeJobs(limit = 20) {
    try {
      const response = await crudOperations.get('/canvassing/scrape/jobs', { limit });
      return response.data;
    } catch (error) {
      console.error('Error fetching scrape jobs:', error);
      throw error;
    }
  },

  async getScrapeJob(jobId) {
    try {
      const response = await crudOperations.get(`/canvassing/scrape/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching scrape job:', error);
      throw error;
    }
  },

  async getTerritories() {
    try {
      const response = await crudOperations.get('/canvassing/scrape/territories');
      return response.data;
    } catch (error) {
      console.error('Error fetching territories:', error);
      throw error;
    }
  },

  async revalidate(jobId, data) {
    try {
      const response = await crudOperations.post(`/canvassing/scrape/${jobId}/validate`, data);
      return response.data;
    } catch (error) {
      console.error('Error starting revalidation:', error);
      throw error;
    }
  },

  async getCoverageStats(params = {}) {
    try {
      const response = await crudOperations.get('/canvassing/scrape/coverage/stats', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching coverage stats:', error);
      throw error;
    }
  },

  async revalidateFiltered(data = {}) {
    try {
      const response = await crudOperations.post('/canvassing/scrape/revalidate', data);
      return response.data;
    } catch (error) {
      console.error('Error starting revalidation:', error);
      throw error;
    }
  },

  // ─── Email Extraction ───────────────────────────────────

  async startEmailExtraction(data) {
    try {
      const response = await crudOperations.post('/canvassing/email/start', data);
      return response.data;
    } catch (error) {
      console.error('Error starting email extraction:', error);
      throw error;
    }
  },

  async cancelEmailExtraction(jobId) {
    try {
      const response = await crudOperations.post(`/canvassing/email/${jobId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling email extraction:', error);
      throw error;
    }
  },

  async getEmailJobs(limit = 20) {
    try {
      const response = await crudOperations.get('/canvassing/email/jobs', { limit });
      return response.data;
    } catch (error) {
      console.error('Error fetching email jobs:', error);
      throw error;
    }
  },

  async getEmailJob(jobId) {
    try {
      const response = await crudOperations.get(`/canvassing/email/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching email job:', error);
      throw error;
    }
  },

  async getEmailStats() {
    try {
      const response = await crudOperations.get('/canvassing/email/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching email stats:', error);
      throw error;
    }
  },

  async getEmailMemberCounts() {
    try {
      const response = await crudOperations.get('/canvassing/email/member-counts');
      return response.data;
    } catch (error) {
      console.error('Error fetching email member counts:', error);
      throw error;
    }
  },

  // ─── Leads ────────────────────────────────────────────────

  async getLeads(params = {}) {
    try {
      const response = await crudOperations.get('/canvassing/leads', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching leads:', error);
      throw error;
    }
  },

  async getLeadStats(params = {}) {
    try {
      const response = await crudOperations.get('/canvassing/leads/stats', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead stats:', error);
      throw error;
    }
  },

  async getMembers() {
    try {
      const response = await crudOperations.get('/canvassing/leads/members');
      return response.data;
    } catch (error) {
      console.error('Error fetching members:', error);
      throw error;
    }
  },

  async getMemberCounts(params = {}) {
    try {
      const response = await crudOperations.get('/canvassing/leads/members/counts', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching member counts:', error);
      throw error;
    }
  },

  async getCategoryCounts(params = {}) {
    try {
      const response = await crudOperations.get('/canvassing/leads/categories/counts', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching category counts:', error);
      throw error;
    }
  },

  async getLeadTowns() {
    try {
      const response = await crudOperations.get('/canvassing/leads/towns');
      return response.data;
    } catch (error) {
      console.error('Error fetching lead towns:', error);
      throw error;
    }
  },

  async deleteLead(id) {
    try {
      const response = await crudOperations.delete(`/canvassing/leads/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting lead:', error);
      throw error;
    }
  },

  async assignLeads(leadIds, userId) {
    try {
      const response = await crudOperations.put('/canvassing/leads/assign', { leadIds, userId });
      return response.data;
    } catch (error) {
      console.error('Error assigning leads:', error);
      throw error;
    }
  },

  async getAssignableUsers() {
    try {
      const response = await crudOperations.get('/canvassing/leads/users');
      return response.data;
    } catch (error) {
      console.error('Error fetching assignable users:', error);
      throw error;
    }
  },

  getExportUrl(params = {}) {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const queryString = new URLSearchParams(params).toString();
    return `${baseUrl}/canvassing/leads/export${queryString ? `?${queryString}` : ''}`;
  }
};

export default canvassingService;
